// POD package PDF.
// One page per POD photo — airports and stops without any POD photo are
// excluded. A skipped ("scheduled in error" / no-show) stop still contributes
// any package PODs the guard actually captured, so real evidence is never
// dropped. Each page is just the stop name at the top and the photo filling
// the rest, with EXIF rotation applied and dark borders auto-cropped.

import jsPDF from "jspdf";
import { saveFileToFolder } from "./saveFolder";
import { buildPodPageList } from "./podPages";

// Luminance threshold (0–255). Rows/cols whose max luminance falls below
// this are treated as dark borders and cropped off.
const CROP_THRESHOLD = 30;
// Don't crop more than this fraction off any single edge — guards against
// cropping a legitimately dark photo down to nothing.
const MAX_CROP_FRACTION = 0.4;

/**
 * Fetch an image, apply EXIF rotation, auto-crop dark borders, return a
 * JPEG data URL + dimensions. Returns null on any failure.
 * Exported: the monthly-bill receipt PDFs use the same loader so receipts
 * behave exactly like POD photos (EXIF, cropping, decode quirks).
 */
export async function loadAndPrepImage(url) {
  if (!url) return null;
  let blob;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn("POD fetch failed", res.status, url);
      return null;
    }
    blob = await res.blob();
  } catch (err) {
    console.warn("POD load error", url, err);
    return null;
  }

  // createImageBitmap handles EXIF orientation natively — no manual rotate math.
  let bitmap;
  try {
    bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
  } catch {
    // Safari/older browsers may not support the option — fall back to raw bitmap.
    try {
      bitmap = await createImageBitmap(blob);
    } catch (err) {
      console.warn("createImageBitmap failed", err);
      return null;
    }
  }

  const { width: w0, height: h0 } = bitmap;
  const canvas = document.createElement("canvas");
  canvas.width = w0;
  canvas.height = h0;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();

  // Auto-crop dark borders.
  let left = 0, right = w0 - 1, top = 0, bottom = h0 - 1;
  try {
    const { data } = ctx.getImageData(0, 0, w0, h0);
    const lumAt = (x, y) => {
      const i = (y * w0 + x) * 4;
      // Rec. 709 luminance
      return 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    };
    const rowHasContent = (y) => {
      for (let x = 0; x < w0; x++) if (lumAt(x, y) > CROP_THRESHOLD) return true;
      return false;
    };
    const colHasContent = (x) => {
      for (let y = 0; y < h0; y++) if (lumAt(x, y) > CROP_THRESHOLD) return true;
      return false;
    };
    const maxV = Math.floor(h0 * MAX_CROP_FRACTION);
    const maxH = Math.floor(w0 * MAX_CROP_FRACTION);
    while (top < maxV && !rowHasContent(top)) top++;
    while (bottom > h0 - 1 - maxV && !rowHasContent(bottom)) bottom--;
    while (left < maxH && !colHasContent(left)) left++;
    while (right > w0 - 1 - maxH && !colHasContent(right)) right--;
  } catch (err) {
    // Tainted canvas shouldn't happen post-CORS fix, but guard anyway.
    console.warn("Auto-crop skipped", err);
    left = 0; right = w0 - 1; top = 0; bottom = h0 - 1;
  }

  const cw = Math.max(1, right - left + 1);
  const ch = Math.max(1, bottom - top + 1);
  const out = document.createElement("canvas");
  out.width = cw;
  out.height = ch;
  out.getContext("2d").drawImage(canvas, left, top, cw, ch, 0, 0, cw, ch);
  return {
    dataUrl: out.toDataURL("image/jpeg", 0.85),
    width: cw,
    height: ch,
    format: "JPEG",
  };
}

/**
 * Generate a POD package PDF for a job. Includes every package POD photo
 * (even on a skipped/no-show stop); airport stops and photo-less stops are
 * omitted.
 * @param {object} job
 * @returns {Promise<Blob>}
 */
export async function generatePodPdf(job) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;
  const contentW = pageW - margin * 2;

  const pages = buildPodPageList(job);
  const jobNo = String(job?.jobNumber || "").trim();

  if (pages.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("No POD photos available for this job.", margin, margin + 20);
    return doc.output("blob");
  }

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    if (i > 0) doc.addPage();

    // Job number, top-right of every page. A POD sheet that gets separated
    // from its package is otherwise unidentifiable — Malca reconcile these
    // against the job number, not the route.
    let labelW = contentW;
    if (jobNo) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(jobNo, pageW - margin, margin + 14, { align: "right" });
      // Reserve what it took, plus a gap, so a long route label shrinks to
      // fit beside it instead of running underneath it.
      labelW = Math.max(60, contentW - doc.getTextWidth(jobNo) - 14);
    }

    doc.setFont("helvetica", "bold");
    // Route-style headers ("X to Y — Receipt No. Z") can outgrow the page
    // width even at the minimum size, and jsPDF doesn't clip — the text just
    // kept running until it collided with the job number, printing
    // "...Receipt No. 3061763MAKY4753". So shrink first, then WRAP whatever
    // still doesn't fit onto a second line.
    let headerSize = 16;
    doc.setFontSize(headerSize);
    while (headerSize > 10 && doc.getTextWidth(page.label) > labelW) {
      headerSize -= 1;
      doc.setFontSize(headerSize);
    }
    let lines = doc.splitTextToSize(page.label, labelW);
    // Two lines is the budget — the photo needs the rest of the page. Keep
    // shrinking until the label fits in them.
    while (lines.length > 2 && headerSize > 7) {
      headerSize -= 1;
      doc.setFontSize(headerSize);
      lines = doc.splitTextToSize(page.label, labelW);
    }
    lines = lines.slice(0, 2);
    const lineGap = headerSize + 2;
    lines.forEach((ln, li) => doc.text(ln, margin, margin + 14 + li * lineGap));

    const imgTop = margin + 30 + (lines.length - 1) * lineGap;
    const maxW = contentW;
    const maxH = pageH - imgTop - margin;

    // eslint-disable-next-line no-await-in-loop
    const img = await loadAndPrepImage(page.photoUrl);
    if (!img) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.text("(POD photo could not be loaded)", margin, imgTop + 20);
      continue;
    }
    const ratio = Math.min(maxW / img.width, maxH / img.height);
    const w = img.width * ratio;
    const h = img.height * ratio;
    const x = margin + (contentW - w) / 2;
    doc.addImage(img.dataUrl, img.format, x, imgTop, w, h);
  }

  return doc.output("blob");
}

// Build the POD filename.
//   Malca: just the numeric parts of the job number — MAKY26-2916 → 26_2916.pdf
//   Else:  PODS_{M_D_YYYY}_{DIR}_JOB_{JOBNUM}.pdf
const DIRECTION_CODE = { outbound: "OB", inbound: "IB", direct: "DR", roundtrip: "RT" };
function podFilename(job) {
  const isMalca = String(job.brokerName || "").toLowerCase().includes("malca");
  if (isMalca) {
    const nums = String(job.jobNumber || "").match(/\d+/g);
    if (nums && nums.length) {
      // Preserve a split-half suffix (…-A / …-B) — it identifies which
      // guard's job this POD set belongs to, so it can't be dropped.
      const half = String(job.jobNumber || "").match(/[AB]$/i);
      const tail = half ? `_${half[0].toUpperCase()}` : "";
      return `${nums.join("_")}${tail}.pdf`;
    }
  }
  let ms = null;
  const ts = job.completedAt || job.completedAtMs || job.startedAt;
  if (typeof ts === "number") ms = ts;
  else if (typeof ts === "string") ms = Date.parse(ts);
  else if (ts?.toMillis) ms = ts.toMillis();
  else if (ts?.seconds != null) ms = ts.seconds * 1000;
  const d = ms != null && !Number.isNaN(ms) ? new Date(ms) : new Date();
  const date = `${d.getMonth() + 1}_${d.getDate()}_${d.getFullYear()}`;
  const dir = DIRECTION_CODE[String(job.jobType || job.type || "").toLowerCase()] || "JOB";
  const num = String(job.jobNumber || job.id || "JOB").replace(/[^A-Za-z0-9]+/g, "_");
  return `PODS_${date}_${dir}_JOB_${num}.pdf`;
}

/** Save the POD package to the chosen folder, else download it. */
export async function downloadPodPdf(job, saveRoot = null, opts = {}) {
  const blob = await generatePodPdf(job);
  if (saveRoot) {
    const path = await saveFileToFolder(saveRoot, job, podFilename(job), blob, opts);
    if (path) return path;
    // fall through to a normal download
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = podFilename(job);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
